import React, { useState } from "react";
import { Button, Form, Card, Spinner } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Import both API functions
import { findSchoolByRollNumber, findSchoolByEmail, findSchoolByIdentifier } from "../services/APIClient";

const SchoolRollNumberInput = ({ onSchoolIdRetrieved, schoolNameRetrieved }) => {
  const [rollNumber, setRollNumber] = useState("");
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    setRollNumber(e.target.value);
  };

  const handleSubmit = async () => {
    if (!rollNumber.trim()) {
      toast.error("Please enter a valid identifier.");
      return;
    }

    setLoading(true);

    try {
      // Use the generic function that auto-detects input type
      const school = await findSchoolByIdentifier(rollNumber.trim());

      if (school && school.id) {
        toast.success("School found successfully!");
        // Pass the target school ID to the parent
        onSchoolIdRetrieved(school.id);
        
        // Determine if we searched by email or roll number
        const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(rollNumber.trim());
        const actualRollNumber = isEmail ? school.rollnumber : rollNumber.trim();
        
        // Pass the school name and the actual roll number
        schoolNameRetrieved(school.name, actualRollNumber);
        console.log("Target School ID set to:", school.id, "School Name:", school.name, "Roll Number:", actualRollNumber);
      } else {
        toast.error(school.message || "No school found with this identifier.");
        // Clear the school data if not found
        onSchoolIdRetrieved("");
        schoolNameRetrieved("Please enter school identifier and press 'Find School'");
      }
    } catch (error) {
      console.error("Error fetching school:", error);
      toast.error("Error fetching the school. Please try again.");
      // Clear the school data on error
      onSchoolIdRetrieved("");
      schoolNameRetrieved("Please enter school identifier and press 'Find School'");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !loading && rollNumber.trim()) {
      handleSubmit();
    }
  };

  return (
    <>
      <ToastContainer 
        position="top-center" 
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <Card className="school-lookup-card shadow-sm">
        <Card.Body className="p-4">
          <div className="d-flex align-items-center mb-3">
            <div className="school-icon me-3">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 3L1 9L12 15L21 9V16H23V9L12 3Z" fill="#0d6efd"/>
                <path d="M5 13.18V17.18C5 17.97 5.53 18.65 6.26 18.93L11.26 20.93C11.74 21.11 12.26 21.11 12.74 20.93L17.74 18.93C18.47 18.65 19 17.97 19 17.18V13.18L12 16.18L5 13.18Z" fill="#0d6efd"/>
              </svg>
            </div>
            <div>
              <h5 className="mb-0 text-primary">School Lookup</h5>
              <small className="text-muted">Find school by identifier</small>
            </div>
          </div>
          
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold text-dark">
              School Identifier
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter roll number, URN, or email address"
              value={rollNumber}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              disabled={loading}
              className="form-control-lg"
              style={{
                borderRadius: '8px',
                border: '2px solid #e9ecef',
                transition: 'all 0.2s ease-in-out'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#0d6efd';
                e.target.style.boxShadow = '0 0 0 0.2rem rgba(13, 110, 253, 0.25)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e9ecef';
                e.target.style.boxShadow = 'none';
              }}
            />
          </Form.Group>
          
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={loading || !rollNumber.trim()}
            className="w-100 py-2"
            style={{
              borderRadius: '8px',
              fontWeight: '600',
              transition: 'all 0.2s ease-in-out'
            }}
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Searching...
              </>
            ) : (
              'Find School'
            )}
          </Button>
        </Card.Body>
      </Card>

      <style jsx>{`
        .school-lookup-card {
          max-width: 400px;
          margin: 0 auto;
          border: none;
          border-radius: 12px;
          background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
        }
        
        .school-lookup-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1) !important;
          transition: all 0.3s ease;
        }
        
        .school-icon {
          width: 48px;
          height: 48px;
          background: rgba(13, 110, 253, 0.1);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .btn-primary {
          background: linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%);
          border: none;
        }
        
        .btn-primary:hover:not(:disabled) {
          background: linear-gradient(135deg, #0a58ca 0%, #084298 100%);
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(13, 110, 253, 0.4);
        }
        
        .btn-primary:disabled {
          background: #6c757d;
          opacity: 0.7;
        }
        
        .form-control-lg:focus {
          transform: translateY(-1px);
        }
        
        @media (max-width: 576px) {
          .school-lookup-card {
            margin: 0 15px;
          }
        }
      `}</style>
    </>
  );
};

export default SchoolRollNumberInput;